/**
 * Page Tools
 */

import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import z from "zod";

import { getPage, searchPages } from "../api/page.js";
import { ApiError } from "../api/errors.js";
import {
  formatPageAsMarkdown,
  extractImageUrlsFromPage,
  replacePageAttachmentReferencesWithResourceUris,
  replacePageImageReferencesWithResourceUris,
  parsePageAttachmentResourceUri,
  parsePageImageResourceUri,
  pageHasAnnotations,
} from "../common/formatters/page.js";
import { getCredentials } from "../common/credentials.js";
import { fetchPageResources } from "../common/pageResource.js";
import type {
  PageResourceResult,
  PageResourceType,
} from "../common/pageResource.js";
import { registerTrackedToolCall } from "../logging.js";
import { ServerContext } from "../types/server.js";

export function registerGetPageTool(server: McpServer, context: ServerContext) {
  const imageResourcesEnabled = context.features["OAK-37_mcp_image_resources"];

  registerTrackedToolCall(
    server,
    "get-page",
    {
      title: "Get a styleguide page",
      description: imageResourcesEnabled
        ? "Call this BEFORE generating UI that uses a documented component, pattern, or guideline. Returns the page's usage notes, recommendations, prop and variant guidance, supporting context, and any file attachments. Treat the response as the source of truth — do not invent props, variants, or usage patterns that are not documented here.\n\n*The response may include embedded asset URIs (`zeroheight://image/...` or `zeroheight://attachment/...`) — call `get-page-asset` with the relevant URIs to fetch those assets as needed.*"
        : "Call this BEFORE generating UI that uses a documented component, pattern, or guideline. Returns the page's usage notes, recommendations, prop and variant guidance, supporting context, and any file attachments. Treat the response as the source of truth — do not invent props, variants, or usage patterns that are not documented here.\n\n*If images from the page are needed, call `get-page-images` afterwards with the same page ID to retrieve them as base64-encoded content. Non-image attachments are linked inline in the page content and can be shared with the user by URL.*",
      inputSchema: {
        pageId: z
          .number()
          .int()
          .describe(
            "Identifies the page containing relevant guidance. Use `list-pages` to locate it. Important: page IDs differ between releases — if using a `releaseId`, call `list-pages` with the same `releaseId` first to get the correct page ID.",
          ),
        releaseId: z
          .number()
          .int()
          .describe(
            "Optional. Do not provide unless the user has explicitly asked for a specific release. Use `list-releases` to discover available releases. When provided, you must also use the page ID from `list-pages` called with this same release ID.",
          )
          .optional(),
      },
      annotations: {
        title: "Get a styleguide page",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async ({ pageId, releaseId }, { authInfo }, clientUserAgent) => {
      const credentials = await getCredentials(authInfo);
      const page = await getPage(
        credentials,
        pageId,
        { releaseId },
        clientUserAgent,
      );

      if (!page) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `Could not find the page. Verify the page ID by calling \`list-pages\`${releaseId ? ` with releaseId ${releaseId}` : ""}.`,
            },
          ],
        };
      }

      const rawTextContent = formatPageAsMarkdown(page);
      const textContent = imageResourcesEnabled
        ? replacePageAttachmentReferencesWithResourceUris(
            pageId,
            replacePageImageReferencesWithResourceUris(pageId, rawTextContent),
          )
        : rawTextContent;

      const annotatedText = pageHasAnnotations(textContent)
        ? `${textContent}\n\n*Note on annotations: marker positions are coordinate metadata only. Without fetching the corresponding image, you can list the labels but cannot determine what visual element each marker points at — be explicit about that limit rather than inferring from coordinates.*`
        : textContent;

      return {
        content: [
          {
            type: "text",
            text: annotatedText,
            mimeType: "text/markdown",
          },
        ],
      };
    },
  );
}

export function registerGetPageAssetTool(
  server: McpServer,
  context: ServerContext,
) {
  if (!context.features["OAK-37_mcp_image_resources"]) {
    return;
  }

  registerTrackedToolCall(
    server,
    "get-page-asset",
    {
      title: "Get an asset from a styleguide page",
      description:
        "Fetch images or attachments from a styleguide page using `zeroheight://image/{pageId}/{imageHash}` or `zeroheight://attachment/{pageId}/{attachmentHash}` URIs returned by `get-page`.",
      inputSchema: {
        assetUris: z
          .array(z.string())
          .min(1)
          .describe(
            "The `zeroheight://image/{pageId}/{imageHash}` or `zeroheight://attachment/{pageId}/{attachmentHash}` URIs copied from `get-page`.",
          ),
      },
      annotations: {
        title: "Get an asset from a styleguide page",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async ({ assetUris }, { authInfo }, clientUserAgent) => {
      const credentials = await getCredentials(authInfo);
      const parsedAssets = assetUris.map((assetUri, index) => ({
        assetUri,
        index,
        parsed: parsePageAssetUri(assetUri),
      }));
      const validAssets = parsedAssets.filter(
        (
          asset,
        ): asset is typeof asset & {
          parsed: NonNullable<(typeof asset)["parsed"]>;
        } => asset.parsed != null,
      );
      const results = await fetchPageResources(
        credentials,
        validAssets.map((asset) => asset.parsed),
        clientUserAgent,
      );
      const resultByIndex = new Map<number, PageResourceResult>();
      validAssets.forEach((asset, index) => {
        resultByIndex.set(asset.index, results[index]);
      });

      const content = parsedAssets.map((asset) => {
        if (!asset.parsed) {
          return {
            type: "text" as const,
            text: `${asset.assetUri}: Invalid asset URI. Use a \`zeroheight://image/{pageId}/{imageHash}\` or \`zeroheight://attachment/{pageId}/{attachmentHash}\` URI returned by \`get-page\`.`,
          };
        }

        const result = resultByIndex.get(asset.index)!;
        return formatPageAssetContent(asset.assetUri, asset.parsed, result);
      });

      return {
        isError: content.every((item) => item.type === "text"),
        content,
      };
    },
  );
}

function formatPageAssetContent(
  assetUri: string,
  parsed: NonNullable<ReturnType<typeof parsePageAssetUri>>,
  result: PageResourceResult,
) {
  if (!result.ok) {
    return {
      type: "text" as const,
      text: `${assetUri}: ${result.message}`,
    };
  }

  if (parsed.resourceType === "image" || result.mimeType.startsWith("image/")) {
    return {
      type: "image" as const,
      data: result.blob,
      mimeType: result.mimeType,
      _meta: {
        uri: result.originalUrl,
      },
    };
  }

  return {
    type: "resource" as const,
    resource: {
      uri: assetUri,
      blob: result.blob,
      mimeType: result.mimeType,
      _meta: { uri: result.originalUrl },
    },
  };
}

function parsePageAssetUri(uri: string): {
  pageId: number;
  resourceHash: string;
  resourceType: PageResourceType;
} | null {
  const image = parsePageImageResourceUri(uri);
  if (image) {
    return {
      pageId: image.pageId,
      resourceHash: image.imageHash,
      resourceType: "image",
    };
  }

  const attachment = parsePageAttachmentResourceUri(uri);
  return attachment
    ? {
        pageId: attachment.pageId,
        resourceHash: attachment.attachmentHash,
        resourceType: "attachment",
      }
    : null;
}

export function registerGetPageImagesTool(
  server: McpServer,
  context: ServerContext,
) {
  if (context.features["OAK-37_mcp_image_resources"]) return;

  registerTrackedToolCall(
    server,
    "get-page-images",
    {
      title: "Get images from a styleguide page",
      description:
        "Fetch all images from a design system page as base64-encoded content. Includes both inline page images and image-typed file attachments.\n\n*Call this after `get-page` with the same page ID when visual examples, screenshots, or diagrams from the page are needed to inform UI generation.*",
      inputSchema: {
        pageId: z
          .number()
          .int()
          .describe(
            "The page to fetch images from. Use the same page ID as a previous `get-page` call. Page IDs differ between releases — ensure the ID matches the release.",
          ),
        releaseId: z
          .number()
          .int()
          .describe(
            "Optional. Do not provide unless the user has explicitly asked for a specific release. Should match the release ID used with `get-page`.",
          )
          .optional(),
      },
      annotations: {
        title: "Get images from a styleguide page",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async ({ pageId, releaseId }, { authInfo }, clientUserAgent) => {
      const credentials = await getCredentials(authInfo);
      const page = await getPage(
        credentials,
        pageId,
        { releaseId },
        clientUserAgent,
      );
      if (!page) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: `Could not find the page. Verify the page ID by calling \`list-pages\`${releaseId ? ` with releaseId ${releaseId}` : ""}.`,
            },
          ],
        };
      }
      const textContent = formatPageAsMarkdown(page);
      const images = await extractImageUrlsFromPage(textContent);
      if (images.length === 0) {
        return {
          content: [
            {
              type: "text",
              text: "No images found on this page.",
            },
          ],
        };
      }
      return {
        content: images,
      };
    },
  );
}

const searchPagesResultSchema = z.object({
  id: z.number().describe("The page ID. Use with `get-page`."),
  type: z.literal("page").describe("The type of search result."),
  name: z.string().describe("The page name."),
  hidden: z.boolean().describe("Whether the page is hidden."),
  locked: z
    .boolean()
    .optional()
    .describe("Whether the page is locked or requires authentication."),
  url: z.string().describe("The page URL."),
  matching_chunk: z
    .string()
    .nullable()
    .describe("The page excerpt that matched the query."),
  score: z.number().describe("The search relevance score."),
});

const searchPagesOutputSchema = {
  results: z
    .array(searchPagesResultSchema)
    .describe("Pages matching the search term, ordered by relevance."),
};

export function registerSearchPagesTool(
  server: McpServer,
  context: ServerContext,
) {
  if (context.features["SKI_2466_bedrock_indexing"] !== true) {
    return;
  }

  const singleStyleguideMode =
    context.connectionMode === "remote_single_styleguide";

  registerTrackedToolCall(
    server,
    "search-pages",
    {
      title: "Search pages in a styleguide",
      description:
        "Search the styleguide for pages matching a term. Use this when you know the component, pattern, or topic you need but not where it lives in the navigation — it's often faster than walking the `list-pages` tree. Follow up with `get-page` on the most relevant result before generating UI.",
      inputSchema: singleStyleguideMode
        ? {
            releaseId: z.number().int().min(1).optional(),
            searchTerm: z
              .string()
              .min(1)
              .describe(
                "The topic, component, pattern, or exact words to find.",
              ),
          }
        : {
            styleguideId: z.number().int().min(1),
            releaseId: z.number().int().min(1).optional(),
            searchTerm: z
              .string()
              .min(1)
              .describe(
                "The topic, component, pattern, or exact words to find.",
              ),
          },
      outputSchema: searchPagesOutputSchema,
      annotations: {
        title: "Search pages in a styleguide",
        readOnlyHint: true,
        destructiveHint: false,
        idempotentHint: true,
        openWorldHint: false,
      },
    },
    async (args, { authInfo }, clientUserAgent) => {
      const credentials = await getCredentials(authInfo);
      const { searchTerm, releaseId } = args as {
        searchTerm: string;
        releaseId?: number;
      };
      const styleguideId =
        context.connectionMode === "remote_single_styleguide"
          ? context.styleguideId
          : (args as { styleguideId: number }).styleguideId;

      if (typeof styleguideId !== "number") {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "Could not determine which styleguide to search.",
            },
          ],
        };
      }

      if (
        credentials.type === "jwt" &&
        credentials.styleguideId !== styleguideId
      ) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "You do not have access to this styleguide",
            },
          ],
        };
      }

      let results;
      try {
        results = await searchPages(
          credentials,
          styleguideId,
          searchTerm,
          releaseId,
          clientUserAgent,
        );
      } catch (e) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text:
                e instanceof ApiError
                  ? e.message
                  : "Could not search pages in this styleguide.",
            },
          ],
        };
      }

      if (results.length === 0) {
        return {
          isError: true,
          content: [
            {
              type: "text",
              text: "No pages match that search term, try something else.",
            },
          ],
        };
      }

      const structuredContent = { results };

      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(structuredContent, null, 2),
            mimeType: "application/json",
          },
        ],
        structuredContent,
      };
    },
  );
}
